import React, { createContext, useState, useEffect } from 'react';
import { authService } from '../services/api';

export const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load token and validate on app startup
  useEffect(() => {
    const validateSession = async () => {
      const savedToken = localStorage.getItem('auth_token');
      const savedUser = localStorage.getItem('auth_user');
      
      if (savedToken && savedUser) {
        try {
          // Validate token with backend
          const currentUser = await authService.getCurrentUser();
          setToken(savedToken);
          setUser(currentUser);
        } catch (error) {
          if (error.response?.status === 401) {
            // Token is invalid/expired - clear everything
            console.log('Session expired or invalid');
            localStorage.removeItem('auth_token');
            localStorage.removeItem('auth_user');
            setToken(null);
            setUser(null);
          } else {
            // Offline or server unreachable: keep the saved session so the
            // app (and offline receipt capture) still works
            setToken(savedToken);
            try {
              setUser(JSON.parse(savedUser));
            } catch {
              setUser(null);
            }
          }
        }
      }
      
      setLoading(false);
    };
    
    validateSession();
  }, []);

  const login = (userData, authToken) => {
    localStorage.setItem('auth_token', authToken);
    localStorage.setItem('auth_user', JSON.stringify(userData));
    setToken(authToken);
    setUser(userData);
  };

  const logout = () => {
    // Drop cached API responses so the next user never sees this user's data
    navigator.serviceWorker?.controller?.postMessage({ type: 'CLEAR_API_CACHE' });
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    setToken(null);
    setUser(null);
  };

  const updateUser = (userData) => {
    localStorage.setItem('auth_user', JSON.stringify(userData));
    setUser(userData);
  };

  const refreshUser = async () => {
    try {
      const currentUser = await authService.getCurrentUser();
      localStorage.setItem('auth_user', JSON.stringify(currentUser));
      setUser(currentUser);
      return currentUser;
    } catch (error) {
      console.error('Error refreshing user:', error);
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, updateUser, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
